import type {
    AggregationSurveyEvaluation,
    SurveyQualityTally,
    SurveySentimentTally,
    SurveyTrendPoint,
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey, SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyInputPurpose, SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {isAnswerableQuestion, SurveyAnswerContext, surveyQuestionTitle} from "../shared/surveyAnswer.util";

/** Why a row has no score: never sent to the AI, nothing answered, not read by the AI yet, or nothing to score. */
export type SummaryRowMuted = 'sensitive' | 'noAnswers' | 'notRead' | 'noScore';

export interface SummaryRow {
    id: string;
    /** As the question cards below number them. */
    number: number;
    title: string;
    isRequired: boolean;
    /** The score is the AI's reading of the comments, not an answer on a scale. */
    fromComments: boolean;
    /** Set on a row without a score, saying why. */
    muted: SummaryRowMuted | null;
    /** 0–100: the average answer, or the AI's reading of the comments; null on a muted row. */
    score: number | null;
    compareScore: number | null;
    /** The answers the score rests on: rated answers, or comments the AI has read. */
    answers: number;
    trend: number | null;
    /** The trend line's ends: the first and the last scored answer in the range. */
    trendStart: SurveyTrendPoint | null;
    trendEnd: SurveyTrendPoint | null;
    spread: number | null;
    compareTrend: number | null;
    compareSpread: number | null;
}

const TEXT_TYPES = [SurveyQuestionType.SHORT_TEXT, SurveyQuestionType.LONG_TEXT];

/** An email or phone field holds contact details, not comments: the API never has the AI read it, so no score. */
export const isCommentQuestion = (question: SurveyQuestion) =>
    question.type != null && TEXT_TYPES.includes(question.type)
    && question.inputPurpose !== SurveyInputPurpose.EMAIL && question.inputPurpose !== SurveyInputPurpose.PHONE;

const qualityScore = (tally: SurveyQualityTally | null | undefined): number | null =>
    tally == null || tally.counted <= 0 ? null : Math.min(Math.max(tally.normalizedSum / tally.counted, 0), 1) * 100;

const commentCount = (tally: SurveySentimentTally | null | undefined) =>
    tally == null ? 0 : tally.positive + tally.neutral + tally.negative;

/** Praise 100, neither 50, complaint 0 - the reading the API draws the trend of a free-text question from. */
const commentScore = (tally: SurveySentimentTally | null | undefined): number | null => {
    const total = commentCount(tally);
    return tally == null || total === 0 ? null : ((tally.positive + tally.neutral / 2) / total) * 100;
};

/** Every question a guest answers, in survey order: scored where it can be, else muted with the reason. */
export function summaryRows(
    survey: Survey,
    evaluation: AggregationSurveyEvaluation | undefined,
    ctx: SurveyAnswerContext
): SummaryRow[] {
    const byId = new Map((evaluation?.questions ?? []).map(entry => [entry.questionId, entry]));

    return (survey.questions ?? [])
        .filter(isAnswerableQuestion)
        .flatMap((question, index): SummaryRow[] => {
            if (question.id == null) return [];

            const entry = byId.get(question.id);
            const row: SummaryRow = {
                id: question.id,
                number: index + 1,
                title: surveyQuestionTitle(question, ctx),
                isRequired: !!question.isRequired,
                fromComments: false,
                muted: null,
                score: null,
                compareScore: null,
                answers: 0,
                trend: entry?.trend ?? null,
                trendStart: entry?.trendStart ?? null,
                trendEnd: entry?.trendEnd ?? null,
                spread: entry?.spread ?? null,
                compareTrend: entry?.compareTrend ?? null,
                compareSpread: entry?.compareSpread ?? null,
            };
            const muted = (reason: SummaryRowMuted): SummaryRow[] => [{
                ...row,
                muted: reason,
                trend: null,
                trendStart: null,
                trendEnd: null,
                spread: null,
                compareTrend: null,
                compareSpread: null,
            }];

            const score = qualityScore(entry?.qualityTally);
            if (score != null) {
                return [{
                    ...row,
                    score,
                    compareScore: qualityScore(entry?.compareQualityTally),
                    answers: entry?.qualityTally?.counted ?? 0,
                }];
            }

            // A rated sensitive question was scored above: only its text is private, never its numbers.
            if (question.isSensitive) return muted('sensitive');
            if ((entry?.answeredCount ?? 0) === 0) return muted('noAnswers');

            if (isCommentQuestion(question)) {
                const comments = commentScore(entry?.sentimentTally);
                if (comments == null) return muted('notRead');

                return [{
                    ...row,
                    fromComments: true,
                    score: comments,
                    compareScore: commentScore(entry?.compareSentimentTally),
                    answers: commentCount(entry?.sentimentTally),
                }];
            }

            return muted('noScore');
        });
}
